// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useMemo,
	useState,
} from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// types
import {
	Entity,
	EntityLoan,
	EntityPayload,
	ErrorCause,
	Feedback,
	Form,
	FormField,
	FormName,
	MetadataField,
	MetadataValue,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useEntityForms } from '../../../hooks/useEntityForms';
import { useFetch } from '../../../hooks/useFetch';

// mui
import {
	Alert,
	AlertTitle,
	Checkbox,
	FormControlLabel,
	Grid,
	SelectChangeEvent,
} from '@mui/material';

// fields
import { DynamicField } from '../../fields/DynamicField/DynamicField';
import { Input } from '../../fields/Input/Input';

// ui
import { Loading } from '../../ui/Loading/Loading';

// utils
import { sanitizeMetadataFieldName } from '../../../utils/metadata';

// props
export interface FormEditBeneficiaryProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditBeneficiary = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormEditBeneficiaryProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();
	const { getDefaultFields } = useEntityForms();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// vars
	const idForm = `form-edit-beneficiary`;
	let formName: FormName = 'BENEFICIARY_INDIVIDUAL';

	// state
	const [beneficiary, setBeneficiary] = useState<Entity | null>(null);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [form, setForm] = useState<Form | null>(null);
	const [searchParams] = useSearchParams();
	const [values, setValues] = useState({
		idExternal: '',
		name: '',
	});

	if (beneficiary?.subtype === 'ORGANIZATION') {
		formName = 'BENEFICIARY_ORGANIZATION';
	}

	// params
	const paramDialogBeneficiary = searchParams.get('dialogBeneficiary');

	// fetch beneficiary
	const { isLoading: isLoadingBeneficiary } = useFetch({
		isEnabled: Boolean(paramDialogBeneficiary),
		url: `${process.env.REACT_APP_API_URL}/entities/${paramDialogBeneficiary}`,
		onSuccess: (res) => {
			if (res.data) {
				const beneficiary = res.data[0] as Entity;

				setBeneficiary(res.data[0]);
				setValues((currentValues) => {
					return {
						...currentValues,
						idExternal: beneficiary?.idExternal as string,
						name: beneficiary?.name as string,
					};
				});
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
	});

	// fetch form
	const { isLoading: isLoadingForm } = useFetch({
		isEnabled: Boolean(beneficiary && project),
		url: `${process.env.REACT_APP_API_URL}/forms?entity=${
			(beneficiary?.loans?.[0] as EntityLoan)?.parent
		}&project=${project?.id}&name=${formName}&status=PUBLISHED`,
		onSuccess: (res) => {
			if (res.data) {
				setForm(res.data[0]);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
	});

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// define form data
		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear error
			setFeedback(null);

			// set loading
			setIsLoading(true);

			if (!beneficiary) {
				throw new Error('The beneficiary does not exist.', {
					cause: {
						id: 'ENTITY_NON_EXISTENT',
					},
				});
			}

			const body: Partial<EntityPayload> = {
				name: String(entries.name),
			};

			const bodyMetadata = [] as MetadataField[];

			// construct body using fields and entries
			for (let i = 0; i < fields.length; i++) {
				const field = fields[i];

				let value: MetadataValue = entries[field.name] ? String(entries[field.name]) : null;

				if (field.type === 'CHECKBOX') {
					value = Boolean(value);
				}

				if (field.type === 'NUMBER') {
					value = Number(value);
				}
				const name = sanitizeMetadataFieldName(field.name);

				bodyMetadata.push({
					isProtected: Boolean(field.isProtected),
					label: field.label,
					name,
					type: field.type,
					value,
				});
			}
			body.metadata = bodyMetadata;

			if (entries.idExternal) {
				body.idExternal = String(entries.idExternal);
			}

			// patch beneficiary
			const reqPatchBeneficiary = await fetch(
				`${process.env.REACT_APP_API_URL}/entities/${beneficiary?.id}`,
				{
					method: 'PATCH',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(body),
				}
			);

			const resPatchBeneficiary = await reqPatchBeneficiary.json();

			if (resPatchBeneficiary.error) {
				throw new Error(resPatchBeneficiary.error.message, {
					cause: {
						id: resPatchBeneficiary.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.beneficiaryUpdated', {
					name: entries.name,
				}),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'ENTITY_DUPLICATE_ID_EXTERNAL') {
					feedbackNew.alert.children = t('error.beneficiaryDuplicateIdExternal', {
						idExternal: entries.idExternal,
					});
				}

				if (cause.id === 'ENTITY_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('error.unauthorized');
				}
			}

			// set error
			setFeedback(feedbackNew);
		} finally {
			// clear loading
			setIsLoading(false);
		}
	};

	// handlers
	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues((currentValues) => {
			return {
				...currentValues,
				[name]: value,
			};
		});
	};

	const fieldsDefault = useMemo(() => {
		return (
			getDefaultFields({ form: formName })?.filter((field) => field.name.includes('metadata')) || []
		);
	}, [formName, getDefaultFields]);

	// define default values for each field
	const fields: FormField[] = useMemo(() => {
		if (beneficiary && form) {
			const metadata = beneficiary.metadata as MetadataField[] | undefined;
			// filter out fields that user doesn't have access to based on metadata and default fields
			const fieldsAllowed = fieldsDefault.filter((field) => {
				const metadataName = sanitizeMetadataFieldName(field.name);
				return metadata?.some((f) => f.name === metadataName);
			});
			return [...fieldsAllowed, ...form.fields].map((field) => {
				if (beneficiary?.metadata) {
					const metadataName = sanitizeMetadataFieldName(field.name);

					const metadataItem = (beneficiary.metadata as MetadataField[]).find(
						(f) => f.name === metadataName
					);
					if (metadataItem) {
						if (field.type === 'CHECKBOX') {
							field.defaultChecked =
								metadataItem.value === undefined ? false : Boolean(metadataItem.value);
						} else {
							field.defaultValue = metadataItem.value;
						}
					}
				}
				return {
					...field,
				};
			});
		}
		return [];
	}, [beneficiary, form, fieldsDefault]);

	const renderContent = () => {
		if (isLoadingBeneficiary || isLoadingForm) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return fields.map((field, i) => {
			return (
				<Grid
					alignSelf={field.type === 'CHECKBOX' ? 'flex-end' : 'flex-start'}
					item
					key={`field-${field.name}-${i}`}
					lg={6}
					xs={12}>
					<DynamicField field={field} />
				</Grid>
			);
		});
	};

	return (
		<form id={idForm} name="formEditBeneficiary" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						helperText={t('idExternal.helper')}
						id={`${idForm}-id-external`}
						label={t('uniqueIdentifier')}
						name="idExternal"
						onChange={handleChangeValue}
						value={values.idExternal}
					/>
				</Grid>
				{renderContent()}
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								checked={true}
								disabled={Boolean(beneficiary)}
								id={`${idForm}-is-kyc-verified`}
								name="isKycVerified"
								required={true}
							/>
						}
						label={t('formAddEntity.kycAml')}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
